// disambiguate — display-level ordinal disambiguation for same-name siblings
// (P18a, resource-identity.md §3).
//
// Two "Fotosyntes" rows render pixel-identical today; nothing enforces title
// uniqueness (rename takes any title; ingest takes the filename; the app even
// mints same-name copies via duplicate_resource). The fix is DISPLAY-level, not
// stored: a pure helper computes, per rendered list, which titles collide and
// hands back a `· N` ordinal for every member of a colliding group. No domain
// change, no event, no backfill — the suffix self-heals on the next fold when a
// sibling is deleted or renamed (a projection-shaped fact, never a stored one).
//
// Symmetric (VS Code's position, not Finder's): ALL colliding items get an
// ordinal, including the first — neither copy is "the real one". Case-sensitive:
// the stored title IS the truth. Ordinals run 1..n in list order; the lists are
// already event-ordered, so the numbering is deterministic and stable across
// renders. The ordinal is a RENDER decoration (dimmed ` · N`), never part of the
// title text — rename dialogs and editors always show the bare stored title.

export type { Disambiguated, TitledItem };
export { disambiguate };

// The minimum a row needs to be disambiguated: an id (map key) and its stored
// title (collision key). Structural — any ResourceRef / tab / palette row fits.
type TitledItem = {
    readonly id: string;
    readonly title: string;
};

// The result: id → its 1-based ordinal, present ONLY for ids whose title
// collides with a sibling. A missing id means "unique in this list — render the
// bare title". A Map keeps lookups O(1) at each render site.
type Disambiguated = ReadonlyMap<string, number>;

// Group by exact title; every member of a group of ≥2 gets its 1-based position
// within the group. Singletons are absent from the result (bare title).
const disambiguate = (items: readonly TitledItem[]): Disambiguated => {
    const byTitle = new Map<string, string[]>();
    for (const item of items) {
        const bucket = byTitle.get(item.title);
        if (bucket === undefined) byTitle.set(item.title, [item.id]);
        else bucket.push(item.id);
    }
    const ordinals = new Map<string, number>();
    for (const ids of byTitle.values()) {
        if (ids.length < 2) continue;
        ids.forEach((id, index) => {
            ordinals.set(id, index + 1);
        });
    }
    return ordinals;
};
